export interface RegentsTestQuestion {
  id: number;
  part: 'I' | 'II' | 'III' | 'IV';
  points: number;
  topic: string;
  standard: string;
  question: string;
  options?: string[];
  correctAnswer?: number;
  solution: string[];
  diagram?: string;
  hint?: string;
  rubric?: string[];
  commonMistakes?: string[];
}

// ─── GEOMETRY REGENTS TEST 2 — PART I (24 MCQ, 2 pts each = 48 pts) ──────────
export const test2PartI: RegentsTestQuestion[] = [
  {
    id: 1, part: 'I', points: 2,
    topic: 'Reflections', standard: 'G.CO.A.4',
    question: 'Point P(5, −3) is reflected over the x-axis. What are the coordinates of P\'?',
    options: ['(−5, −3)', '(5, 3)', '(−5, 3)', '(3, −5)'],
    correctAnswer: 1,
    solution: [
      'Reflection over x-axis rule: (x, y) → (x, −y)',
      'P(5, −3) → P\'(5, 3)',
      'Answer: (5, 3)'
    ]
  },
  {
    id: 2, part: 'I', points: 2,
    topic: 'Triangle Inequality', standard: 'G.CO.C.10',
    question: 'Which set of side lengths can form a triangle?',
    options: ['3, 4, 8', '5, 7, 11', '2, 5, 8', '6, 8, 15'],
    correctAnswer: 1,
    solution: [
      'Triangle Inequality: sum of any two sides must be greater than the third side.',
      'Check 5, 7, 11: 5+7=12>11 ✓, 5+11=16>7 ✓, 7+11=18>5 ✓',
      'All three conditions satisfied.',
      'Answer: 5, 7, 11'
    ]
  },
  {
    id: 3, part: 'I', points: 2,
    topic: 'Parallel Lines', standard: 'G.CO.C.9',
    question: 'Lines m and n are parallel. A transversal cuts them forming angle 1 = 115°. What is the measure of the corresponding angle on line n?',
    options: ['65°', '115°', '25°', '90°'],
    correctAnswer: 1,
    solution: [
      'Corresponding angles formed by parallel lines and a transversal are congruent.',
      'Corresponding angle = 115°',
      'Answer: 115°'
    ]
  },
  {
    id: 4, part: 'I', points: 2,
    topic: 'Distance Formula', standard: 'G.GPE.B.7',
    question: 'What is the distance between points A(2, 5) and B(6, 8)?',
    options: ['5', '7', '√7', '√25'],
    correctAnswer: 0,
    solution: [
      'd = √[(x₂−x₁)² + (y₂−y₁)²]',
      'd = √[(6−2)² + (8−5)²]',
      'd = √[16 + 9] = √25 = 5',
      'Answer: 5'
    ]
  },
  {
    id: 5, part: 'I', points: 2,
    topic: 'Circle — Chord', standard: 'G.C.A.2',
    question: 'A chord is 24 units long and is 5 units from the center of a circle. What is the radius?',
    options: ['13', '12', '29', '√119'],
    correctAnswer: 0,
    solution: [
      'The perpendicular from center to chord bisects the chord.',
      'Half-chord = 24/2 = 12',
      'Right triangle: r² = 12² + 5² = 144 + 25 = 169',
      'r = √169 = 13',
      'Answer: 13'
    ]
  },
  {
    id: 6, part: 'I', points: 2,
    topic: 'Volume — Cone', standard: 'G.GMD.A.3',
    question: 'A cone has radius 6 and height 8. What is its volume?',
    options: ['96π', '144π', '48π', '288π'],
    correctAnswer: 0,
    solution: [
      'Volume of cone = (1/3)πr²h',
      'V = (1/3)π(6)²(8)',
      'V = (1/3)π(36)(8) = (1/3)(288π) = 96π',
      'Answer: 96π'
    ]
  },
  {
    id: 7, part: 'I', points: 2,
    topic: 'Trigonometry — Sine', standard: 'G.SRT.C.8',
    question: 'In right triangle ABC with right angle at C, sin(A) = 5/13. If AB = 26, what is BC?',
    options: ['10', '24', '5', '13'],
    correctAnswer: 0,
    solution: [
      'sin(A) = opposite/hypotenuse = BC/AB',
      '5/13 = BC/26',
      'BC = 26 × (5/13) = 10',
      'Answer: 10'
    ]
  },
  {
    id: 8, part: 'I', points: 2,
    topic: 'Rotation', standard: 'G.CO.A.5',
    question: 'Point Q(4, 2) is rotated 180° about the origin. What are the coordinates of Q\'?',
    options: ['(−4, −2)', '(4, −2)', '(−4, 2)', '(2, 4)'],
    correctAnswer: 0,
    solution: [
      'Rotation 180° about origin: (x, y) → (−x, −y)',
      'Q(4, 2) → Q\'(−4, −2)',
      'Answer: (−4, −2)'
    ]
  },
  {
    id: 9, part: 'I', points: 2,
    topic: 'Parallelogram Properties', standard: 'G.CO.C.11',
    question: 'In parallelogram ABCD, angle A = 68°. What is angle C?',
    options: ['112°', '68°', '22°', '136°'],
    correctAnswer: 1,
    solution: [
      'In a parallelogram, opposite angles are congruent.',
      'Angle C = Angle A = 68°',
      'Answer: 68°'
    ]
  },
  {
    id: 10, part: 'I', points: 2,
    topic: 'Dilation', standard: 'G.SRT.A.1',
    question: 'Triangle DEF is dilated by scale factor 1/2 centered at the origin. If D = (8, 6), what is D\'?',
    options: ['(4, 3)', '(16, 12)', '(8, 3)', '(4, 6)'],
    correctAnswer: 0,
    solution: [
      'Dilation: (x, y) → (kx, ky) where k = 1/2',
      'D\' = ((1/2)×8, (1/2)×6) = (4, 3)',
      'Answer: (4, 3)'
    ]
  },
  {
    id: 11, part: 'I', points: 2,
    topic: 'Inscribed Angle', standard: 'G.C.A.2',
    question: 'An inscribed angle intercepts an arc of 140°. What is the measure of the inscribed angle?',
    options: ['70°', '140°', '280°', '35°'],
    correctAnswer: 0,
    solution: [
      'Inscribed Angle Theorem: inscribed angle = (1/2) × intercepted arc',
      'Inscribed angle = (1/2)(140°) = 70°',
      'Answer: 70°'
    ]
  },
  {
    id: 12, part: 'I', points: 2,
    topic: 'Slope', standard: 'G.GPE.B.5',
    question: 'What is the slope of a line parallel to y = −3x + 7?',
    options: ['3', '−3', '1/3', '−1/3'],
    correctAnswer: 1,
    solution: [
      'Parallel lines have equal slopes.',
      'Slope of y = −3x + 7 is −3.',
      'Parallel slope = −3',
      'Answer: −3'
    ]
  },
  {
    id: 13, part: 'I', points: 2,
    topic: 'Triangle Congruence', standard: 'G.CO.B.8',
    question: 'Which condition does NOT guarantee triangle congruence?',
    options: ['HL', 'ASA', 'SSA', 'SAS'],
    correctAnswer: 2,
    solution: [
      'SSA (Side-Side-Angle) is NOT a valid congruence criterion.',
      'It can produce two different triangles (ambiguous case).',
      'HL, ASA, and SAS are all valid.',
      'Answer: SSA'
    ]
  },
  {
    id: 14, part: 'I', points: 2,
    topic: 'Exterior Angle', standard: 'G.CO.C.10',
    question: 'In triangle PQR, angle P = 45° and angle Q = 70°. What is the measure of the exterior angle at R?',
    options: ['115°', '65°', '245°', '125°'],
    correctAnswer: 0,
    solution: [
      'Exterior Angle Theorem: exterior angle = sum of two remote interior angles',
      'Exterior angle at R = angle P + angle Q = 45° + 70° = 115°',
      'Answer: 115°'
    ]
  },
  {
    id: 15, part: 'I', points: 2,
    topic: 'Surface Area — Cylinder', standard: 'G.GMD.A.3',
    question: 'A cylinder has radius 4 and height 10. What is its total surface area?',
    options: ['112π', '160π', '80π', '40π'],
    correctAnswer: 0,
    solution: [
      'SA = 2πr² + 2πrh = 2πr(r + h)',
      'SA = 2π(4)(4 + 10) = 2π(4)(14) = 112π',
      'Answer: 112π'
    ]
  },
  {
    id: 16, part: 'I', points: 2,
    topic: 'Special Right Triangles', standard: 'G.SRT.C.8',
    question: 'In a 45-45-90 triangle, the hypotenuse is 10√2. What is the length of each leg?',
    options: ['10', '5√2', '20', '5'],
    correctAnswer: 0,
    solution: [
      'In 45-45-90 triangle: sides are in ratio 1 : 1 : √2',
      'leg : leg : hypotenuse = x : x : x√2',
      'x√2 = 10√2',
      'x = 10',
      'Answer: 10'
    ]
  },
  {
    id: 17, part: 'I', points: 2,
    topic: 'Midpoint', standard: 'G.GPE.B.6',
    question: 'The midpoint of segment AB is M(3, 5). If A = (1, 2), what are the coordinates of B?',
    options: ['(5, 8)', '(2, 3.5)', '(4, 7)', '(5, 3)'],
    correctAnswer: 0,
    solution: [
      'Midpoint formula: M = ((x₁+x₂)/2, (y₁+y₂)/2)',
      '(3, 5) = ((1+x₂)/2, (2+y₂)/2)',
      '3 = (1+x₂)/2 → x₂ = 5',
      '5 = (2+y₂)/2 → y₂ = 8',
      'B = (5, 8)',
      'Answer: (5, 8)'
    ]
  },
  {
    id: 18, part: 'I', points: 2,
    topic: 'Similarity Ratio', standard: 'G.SRT.A.2',
    question: 'Two similar rectangles have perimeters 20 and 50. What is the ratio of their areas?',
    options: ['4:25', '2:5', '20:50', '400:2500'],
    correctAnswer: 0,
    solution: [
      'Perimeter ratio = side ratio = 20:50 = 2:5',
      'Area ratio = (side ratio)² = (2:5)² = 4:25',
      'Answer: 4:25'
    ]
  },
  {
    id: 19, part: 'I', points: 2,
    topic: 'Central Angle', standard: 'G.C.B.5',
    question: 'In circle O with radius 9, central angle AOB = 80°. What is the length of arc AB?',
    options: ['4π', '2π', '8π', '6π'],
    correctAnswer: 0,
    solution: [
      'Arc length = (θ/360°) × 2πr',
      'Arc AB = (80°/360°) × 2π(9)',
      'Arc AB = (2/9) × 18π = 4π',
      'Answer: 4π'
    ]
  },
  {
    id: 20, part: 'I', points: 2,
    topic: 'Equation of Circle', standard: 'G.GPE.A.1',
    question: 'What is the center of the circle x² + y² − 6x + 4y = 12?',
    options: ['(3, −2)', '(−3, 2)', '(6, −4)', '(−6, 4)'],
    correctAnswer: 0,
    solution: [
      'Complete the square: (x² − 6x) + (y² + 4y) = 12',
      '(x² − 6x + 9) + (y² + 4y + 4) = 12 + 9 + 4',
      '(x − 3)² + (y + 2)² = 25',
      'Center = (3, −2)',
      'Answer: (3, −2)'
    ]
  },
  {
    id: 21, part: 'I', points: 2,
    topic: 'Altitude to Hypotenuse', standard: 'G.SRT.B.5',
    question: 'In a right triangle, the altitude to the hypotenuse divides it into segments of 3 and 12. What is the length of the altitude?',
    options: ['6', '9', '15', '√15'],
    correctAnswer: 0,
    solution: [
      'Geometric Mean Theorem: altitude² = product of segments',
      'altitude² = 3 × 12 = 36',
      'altitude = 6',
      'Answer: 6'
    ]
  },
  {
    id: 22, part: 'I', points: 2,
    topic: 'Composition of Transformations', standard: 'G.CO.A.5',
    question: 'A figure is translated 3 units right, then reflected over the y-axis. Point (2, 5) becomes what point?',
    options: ['(−5, 5)', '(5, 5)', '(−1, 5)', '(1, 5)'],
    correctAnswer: 0,
    solution: [
      'Translation: (2, 5) → (2+3, 5) = (5, 5)',
      'Reflection over y-axis: (5, 5) → (−5, 5)',
      'Answer: (−5, 5)'
    ]
  },
  {
    id: 23, part: 'I', points: 2,
    topic: 'Polygon Angles', standard: 'G.CO.C.10',
    question: 'What is the sum of the interior angles of a hexagon?',
    options: ['720°', '1080°', '540°', '900°'],
    correctAnswer: 0,
    solution: [
      'Sum of interior angles = (n − 2) × 180°',
      'For hexagon (n = 6): (6 − 2) × 180° = 4 × 180° = 720°',
      'Answer: 720°'
    ]
  },
  {
    id: 24, part: 'I', points: 2,
    topic: 'Cross-Section', standard: 'G.GMD.B.4',
    question: 'A plane intersects a sphere. What is the cross-section?',
    options: ['Circle', 'Ellipse', 'Rectangle', 'Triangle'],
    correctAnswer: 0,
    solution: [
      'Any plane intersecting a sphere creates a circular cross-section.',
      'The size varies but the shape is always a circle.',
      'Answer: Circle'
    ]
  }
];

// ─── TEST 2 — PART II (7 questions, 2 pts each = 14 pts) ─────────────────────
export const test2PartII: RegentsTestQuestion[] = [
  {
    id: 25, part: 'II', points: 2,
    topic: 'Transformations', standard: 'G.CO.A.4',
    question: 'Triangle ABC has vertices A(2, 3), B(5, 1), and C(4, 6). Find the coordinates of triangle A\'B\'C\' after a translation of 〈−3, 4〉.',
    hint: 'Translation rule: (x, y) → (x + h, y + k)',
    solution: [
      'Translation vector: 〈−3, 4〉 means move left 3, up 4',
      'A(2, 3) → A\'(2−3, 3+4) = A\'(−1, 7)',
      'B(5, 1) → B\'(5−3, 1+4) = B\'(2, 5)',
      'C(4, 6) → C\'(4−3, 6+4) = C\'(1, 10)',
      'Image vertices: A\'(−1, 7), B\'(2, 5), C\'(1, 10)'
    ],
    rubric: [
      '2 points: All three coordinates correct.',
      '1 point: Two coordinates correct.',
      '0 points: Fewer than two correct or no work shown.'
    ]
  },
  {
    id: 26, part: 'II', points: 2,
    topic: 'Pythagorean Theorem', standard: 'G.SRT.B.4',
    question: 'A rectangular garden is 15 feet long and 8 feet wide. What is the length of the diagonal?',
    hint: 'The diagonal forms the hypotenuse of a right triangle.',
    solution: [
      'Use Pythagorean Theorem: c² = a² + b²',
      'c² = 15² + 8²',
      'c² = 225 + 64 = 289',
      'c = √289 = 17',
      'The diagonal is 17 feet.'
    ],
    rubric: [
      '2 points: Correct setup and answer 17 ft.',
      '1 point: Correct equation but arithmetic error.',
      '0 points: Wrong formula or no work.'
    ]
  },
  {
    id: 27, part: 'II', points: 2,
    topic: 'Angle Bisector', standard: 'G.CO.C.9',
    question: 'Ray BD bisects angle ABC. If angle ABD = (3x + 10)° and angle DBC = (5x − 6)°, find x and the measure of angle ABC.',
    hint: 'An angle bisector divides an angle into two congruent angles.',
    solution: [
      'Since BD bisects angle ABC: angle ABD = angle DBC',
      '3x + 10 = 5x − 6',
      'Subtract 3x: 10 = 2x − 6',
      'Add 6: 16 = 2x',
      'x = 8',
      'Angle ABD = 3(8) + 10 = 34°',
      'Angle ABC = 2 × 34° = 68°'
    ],
    rubric: [
      '2 points: Correct x = 8 and angle ABC = 68°.',
      '1 point: Correct equation but arithmetic error.',
      '0 points: Wrong relationship or no work.'
    ]
  },
  {
    id: 28, part: 'II', points: 2,
    topic: 'Area of Triangle', standard: 'G.GPE.B.7',
    question: 'Triangle RST has vertices R(0, 0), S(8, 0), and T(4, 5). Find the area of the triangle.',
    hint: 'Use Area = (1/2) × base × height. The base RS lies on the x-axis.',
    solution: [
      'Base RS lies on x-axis from (0,0) to (8,0)',
      'Base = 8 units',
      'Height = perpendicular distance from T to x-axis = 5 units',
      'Area = (1/2) × base × height',
      'Area = (1/2) × 8 × 5 = 20 square units'
    ],
    rubric: [
      '2 points: Correct base, height, and area 20.',
      '1 point: Correct formula but wrong height.',
      '0 points: Wrong formula or no work.'
    ]
  },
  {
    id: 29, part: 'II', points: 2,
    topic: 'Circle — Tangent', standard: 'G.C.A.2',
    question: 'From point P, two tangent segments are drawn to circle O. If PA = 2x + 7 and PB = 4x − 5, find the length of each tangent.',
    hint: 'Tangent segments from the same external point are congruent.',
    solution: [
      'Theorem: PA = PB (tangents from same point)',
      '2x + 7 = 4x − 5',
      'Subtract 2x: 7 = 2x − 5',
      'Add 5: 12 = 2x',
      'x = 6',
      'PA = 2(6) + 7 = 19',
      'Each tangent segment is 19 units.'
    ],
    rubric: [
      '2 points: Correct x = 6 and length 19.',
      '1 point: Correct equation but arithmetic error.',
      '0 points: Theorem not applied.'
    ]
  },
  {
    id: 30, part: 'II', points: 2,
    topic: 'Volume — Pyramid', standard: 'G.GMD.A.3',
    question: 'A square pyramid has a base edge of 6 meters and a height of 8 meters. Find the volume.',
    hint: 'Volume of pyramid = (1/3) × base area × height',
    solution: [
      'Base is a square with side 6 m',
      'Base area = 6² = 36 m²',
      'Height = 8 m',
      'Volume = (1/3) × base area × height',
      'V = (1/3) × 36 × 8 = (1/3) × 288 = 96 m³'
    ],
    rubric: [
      '2 points: Correct base area and volume 96 m³.',
      '1 point: Correct formula but arithmetic error.',
      '0 points: Wrong formula or no work.'
    ]
  },
  {
    id: 31, part: 'II', points: 2,
    topic: 'Distance & Midpoint', standard: 'G.GPE.B.6',
    question: 'Segment CD has endpoints C(−2, 4) and D(4, 12). Find the midpoint M and the length of CD in simplest radical form.',
    hint: 'Use midpoint and distance formulas.',
    solution: [
      'Midpoint: M = ((−2+4)/2, (4+12)/2) = (2/2, 16/2) = (1, 8)',
      '',
      'Distance: d = √[(4−(−2))² + (12−4)²]',
      'd = √[(6)² + (8)²]',
      'd = √[36 + 64] = √100 = 10',
      '',
      'Midpoint M = (1, 8), Length = 10'
    ],
    rubric: [
      '2 points: Both midpoint and length correct.',
      '1 point: One of the two correct.',
      '0 points: Both incorrect or no work.'
    ]
  }
];

// ─── TEST 2 — PART III (3 questions, 4 pts each = 12 pts) ────────────────────
export const test2PartIII: RegentsTestQuestion[] = [
  {
    id: 32, part: 'III', points: 4,
    topic: 'Triangle Similarity Proof', standard: 'G.SRT.A.3',
    question: 'Given: In triangle ABC, DE is parallel to BC, with D on AB and E on AC. Prove: Triangle ADE ~ Triangle ABC.',
    diagram: `
         A
        /|\\
       / | \\
      D──E  \\
     /   |   \\
    B────+────C
    (DE ∥ BC)
`,
    hint: 'Use AA (Angle-Angle) similarity criterion.',
    solution: [
      'PROOF:',
      '',
      'Statement 1: DE ∥ BC',
      'Reason 1: Given',
      '',
      'Statement 2: ∠ADE ≅ ∠ABC',
      'Reason 2: Corresponding angles (parallel lines cut by transversal AB)',
      '',
      'Statement 3: ∠AED ≅ ∠ACB',
      'Reason 3: Corresponding angles (parallel lines cut by transversal AC)',
      '',
      'Statement 4: ∠A ≅ ∠A',
      'Reason 4: Reflexive Property',
      '',
      'Statement 5: △ADE ~ △ABC',
      'Reason 5: AA Similarity (two pairs of congruent angles)',
      '',
      'Conclusion: Triangle ADE is similar to triangle ABC. ∎'
    ],
    rubric: [
      '4 points: All statements with correct reasons, AA similarity identified.',
      '3 points: Most statements correct, similarity criterion stated.',
      '2 points: Two angle pairs identified correctly.',
      '1 point: Some correct work but incomplete proof.',
      '0 points: No valid proof structure.'
    ]
  },
  {
    id: 33, part: 'III', points: 4,
    topic: 'Right Triangle Trigonometry', standard: 'G.SRT.C.8',
    question: 'A kite is flying at the end of a 100-meter string. The string makes an angle of 65° with the ground. How high is the kite above the ground? Round to the nearest meter.',
    diagram: `
    Kite
     *
     |\\
     | \\ 100 m
   h |  \\
     |   \\
     |    \\
     +─────* Ground
       65°
`,
    hint: 'Use sine: sin(θ) = opposite/hypotenuse',
    solution: [
      'Set up right triangle: hypotenuse = 100 m, angle = 65°',
      'Height h is opposite to the 65° angle',
      'sin(65°) = h/100',
      'h = 100 × sin(65°)',
      'sin(65°) ≈ 0.9063',
      'h ≈ 100 × 0.9063',
      'h ≈ 90.63',
      'h ≈ 91 meters',
      'The kite is approximately 91 meters high.'
    ],
    rubric: [
      '4 points: Correct trig function, setup, and answer 91 m.',
      '3 points: Correct function and setup, minor rounding error.',
      '2 points: Correct function but setup error.',
      '1 point: Some correct work but wrong function.',
      '0 points: No valid approach.'
    ]
  },
  {
    id: 34, part: 'III', points: 4,
    topic: 'Coordinate Geometry — Parallelogram', standard: 'G.GPE.B.4',
    question: 'Quadrilateral PQRS has vertices P(1, 2), Q(6, 2), R(8, 6), S(3, 6). Prove that PQRS is a parallelogram and determine if it is a rhombus.',
    diagram: `
    y
  6 | S(3,6)────────R(8,6)
  5 |  /              /
  4 | /              /
  3 |/              /
  2 |P(1,2)────────Q(6,2)
  1 |
  0 +──────────────────── x
    0  1  2  3  4  5  6  7  8
`,
    hint: 'Show opposite sides parallel (equal slopes). For rhombus, check if all sides are equal length.',
    solution: [
      'STEP 1: Prove parallelogram (opposite sides parallel)',
      '',
      'Slope PQ = (2−2)/(6−1) = 0/5 = 0',
      'Slope SR = (6−6)/(8−3) = 0/5 = 0',
      'PQ ∥ SR ✓',
      '',
      'Slope PS = (6−2)/(3−1) = 4/2 = 2',
      'Slope QR = (6−2)/(8−6) = 4/2 = 2',
      'PS ∥ QR ✓',
      '',
      'Both pairs of opposite sides parallel → PQRS is a parallelogram.',
      '',
      'STEP 2: Check if rhombus (all sides equal)',
      '',
      'PQ = √[(6−1)² + (2−2)²] = √25 = 5',
      'SR = √[(8−3)² + (6−6)²] = √25 = 5',
      'PS = √[(3−1)² + (6−2)²] = √[4+16] = √20 = 2√5',
      'QR = √[(8−6)² + (6−2)²] = √[4+16] = √20 = 2√5',
      '',
      'PQ = SR = 5, but PS = QR = 2√5 ≠ 5',
      'Not all sides equal → NOT a rhombus.',
      '',
      'Conclusion: PQRS is a parallelogram but NOT a rhombus. ∎'
    ],
    rubric: [
      '4 points: Parallelogram proven with slopes, rhombus check with side lengths, correct conclusion.',
      '3 points: Parallelogram proven correctly, rhombus check incomplete.',
      '2 points: Two slopes correct and one pair parallel shown.',
      '1 point: Some correct calculations but no valid conclusion.',
      '0 points: No valid coordinate geometry work.'
    ]
  }
];

// ─── TEST 2 — PART IV (1 question, 6 pts) ────────────────────────────────────
export const test2PartIV: RegentsTestQuestion[] = [
  {
    id: 35, part: 'IV', points: 6,
    topic: 'Composite Solids — Real-World Modeling', standard: 'G.MG.A.1',
    question: `A water tower consists of a cylinder with a cone on top. The cylinder has radius 5 feet and height 12 feet. The cone has the same radius (5 feet) and height 6 feet.

(a) Find the total volume of the water tower (cylinder + cone). Leave your answer in terms of π.

(b) If the tower is filled to 80% capacity, how many cubic feet of water does it hold? Round to the nearest cubic foot.

(c) The entire outer surface (lateral surface of cylinder + lateral surface of cone, NOT the base) is to be painted. Find the total surface area to be painted. Leave your answer in terms of π. (Hint: slant height of cone l = √(r² + h²))`,
    diagram: `
       /\\
      /  \\  ← Cone (r=5, h=6)
     /____\\
     |    |
     |    |  ← Cylinder (r=5, h=12)
     |    |
     |____|
      base
`,
    hint: 'Volume: cylinder = πr²h, cone = (1/3)πr²h. Lateral SA: cylinder = 2πrh, cone = πrl',
    solution: [
      '═══ PART (a): Total Volume ═══',
      '',
      'Volume of cylinder = πr²h = π(5)²(12) = π(25)(12) = 300π ft³',
      '',
      'Volume of cone = (1/3)πr²h = (1/3)π(5)²(6) = (1/3)π(25)(6) = 50π ft³',
      '',
      'Total volume = 300π + 50π = 350π ft³',
      '',
      '═══ PART (b): 80% of Total Volume ═══',
      '',
      'Water volume = 0.80 × 350π',
      '= 280π',
      '≈ 280 × 3.14159',
      '≈ 879.6',
      '≈ 880 cubic feet',
      '',
      'The tower holds approximately 880 cubic feet of water.',
      '',
      '═══ PART (c): Painted Surface Area ═══',
      '',
      'Lateral SA of cylinder = 2πrh = 2π(5)(12) = 120π ft²',
      '',
      'Slant height of cone: l = √(r² + h²) = √(5² + 6²) = √(25 + 36) = √61',
      'Lateral SA of cone = πrl = π(5)(√61) = 5π√61 ft²',
      '',
      'Total painted area = 120π + 5π√61 ft²',
      '',
      'Total painted surface area = (120 + 5√61)π ft² ≈ 159.03π ft²'
    ],
    rubric: [
      '6 points: All three parts fully correct with work shown.',
      '5 points: Two parts fully correct, third has minor error.',
      '4 points: Parts (a) and (c) correct, part (b) has error.',
      '3 points: Two of three parts correct.',
      '2 points: One part fully correct.',
      '1 point: Correct formulas but multiple errors.',
      '0 points: No valid work.'
    ],
    commonMistakes: [
      'Using full cone volume formula without (1/3).',
      'Forgetting to find slant height for cone lateral surface.',
      'Including the base in surface area (problem says NOT the base).',
      'Not converting 80% to 0.80.',
      'Using diameter instead of radius.'
    ]
  }
];